/**
 * Addons API
 *
 * Network-only functions for flight addons operations.
 * NO Redux imports allowed in this file.
 *
 * Uses common flights API utilities which automatically add:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildQueryParams, buildHeaders, buildUrl } from '@/api/utils';
import { logger } from '@/shared/utils/logger';
import type {
  FlightsAddonsComponentData,
  FlightsAddonMealsComponentData,
  FlightsAddonBaggageComponentData,
  FlightsCouponsComponentData,
  FlightsSeatsComponentData,
  ReviewPrice,
  FareBreakup,
  SeatsData,
  FetchSeatsRequestParams,
} from '@contracts';
import type {
  FetchAddonsRequestParams,
  AddonSelectionRequestParams,
  AddonSelectionRequestBody,
  AddonSelectionResponse,
  FetchMealsRequestParams,
  FetchBaggageRequestParams,
  FetchCouponsRequestParams,
} from '../types';

// ============================================
// Error Classes
// ============================================

export class FetchAddonsError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FetchAddonsError';
  }
}

export class AddonSelectionError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'AddonSelectionError';
  }
}

export class FetchMealsError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FetchMealsError';
  }
}

export class FetchBaggageError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FetchBaggageError';
  }
}

export class FetchCouponsError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FetchCouponsError';
  }
}

export class FetchSeatsError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FetchSeatsError';
  }
}

// ============================================
// API Functions
// ============================================

/**
 * API error response structure
 * Supports multiple formats:
 * - { error: { code, message } } - Legacy format
 * - { responseStatus: "FAILED", errorDetails: { errorDescription } } - New format
 */
interface ApiErrorResponse {
  readonly error?: {
    readonly code?: string;
    readonly message?: string;
  };
  readonly responseStatus?: string;
  readonly errorDetails?: {
    readonly errorCode?: string;
    readonly errorMessage?: string;
    readonly errorDescription?: string;
    readonly serviceName?: string;
    readonly statusCode?: number;
    readonly owner?: string;
  };
}

/**
 * Extract error message from API response
 * Handles multiple error formats:
 * 1. New format: { responseStatus: "FAILED", errorDetails: { errorDescription } }
 * 2. Legacy format: { error: { message } }
 */
function extractErrorMessage(data: ApiErrorResponse): string | undefined {
  // Check new format first: { responseStatus: "FAILED", errorDetails: { errorDescription } }
  if (data.responseStatus === 'FAILED' && data.errorDetails?.errorDescription) {
    return data.errorDetails.errorDescription;
  }
  // Check legacy nested format: { error: { message } }
  if (data.error?.message) {
    return data.error.message;
  }
  return undefined;
}

/**
 * Fetch addons
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchAddons(
  params: FetchAddonsRequestParams,
): Promise<FlightsAddonsComponentData> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
    cc: params.cabinClass ?? 'E',
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.ADDONS_ANCILLARY, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    // Always try to parse response body to get error message
    const data = (await response.json()) as FlightsAddonsComponentData & ApiErrorResponse;

    // Check if API returned an error in the response body (supports both formats)
    const errorMessage = extractErrorMessage(data);
    if (errorMessage) {
      throw new FetchAddonsError(errorMessage, response.status);
    }

    if (!response.ok) {
      throw new FetchAddonsError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof FetchAddonsError) {
      throw error;
    }

    throw new FetchAddonsError('Failed to fetch addons', undefined, error);
  }
}

/**
 * Raw API response structure (flexible to handle different backend formats)
 */
interface RawAddonSelectionResponse {
  readonly success?: boolean;
  readonly message?: string;
  // Nested fareSummary format
  readonly fareSummary?: {
    readonly reviewPrice?: unknown;
    readonly fareBreakup?: unknown;
  };
  // Direct format (reviewPrice and fareBreakup at root level)
  readonly reviewPrice?: unknown;
  readonly fareBreakup?: unknown;
  // Alternative nested format under 'data'
  readonly data?: {
    readonly reviewPrice?: unknown;
    readonly fareBreakup?: unknown;
    readonly seats?: unknown;
  };
  // Coupon data (returned after coupon selection)
  readonly couponData?: unknown;
  // Applied coupon data (alternative field name from API)
  readonly appliedCoupon?: unknown;
  // Seats data (returned after seat selection)
  readonly seats?: unknown;
  // CTA list (may be returned after baggage selection)
  readonly ctaList?: unknown;
  // Baggage data (returned after baggage selection with updated cta.text)
  readonly baggage?: unknown;
  // Addons data (returned after addon selection with updated cta.text and isSelected)
  readonly addons?: unknown;
  // Coupons data (returned after coupon selection with updated isSelected)
  readonly coupons?: unknown;
  // Meals data (returned after meal selection with updated selectedCount)
  readonly meals?: unknown;
  // Additional nested structures that might contain fareBreakup
  readonly resources?: unknown;
}

/**
 * Normalize API response to handle different backend formats
 * Supports:
 * - { fareSummary: { reviewPrice?, fareBreakup? } }
 * - { reviewPrice?, fareBreakup? } (direct at root)
 * - { data: { reviewPrice?, fareBreakup?, seats? } }
 * - { seats } (direct at root or nested in data)
 * Note: Either reviewPrice or fareBreakup (or both) can be present
 */
function normalizeAddonSelectionResponse(raw: RawAddonSelectionResponse): AddonSelectionResponse {
  const normalized: AddonSelectionResponse = { data: {} };

  normalized.data.fareSummary = {};

  if (raw.fareSummary?.reviewPrice || raw.fareSummary?.fareBreakup) {
    if (raw.fareSummary.reviewPrice) {
      normalized.data.fareSummary.reviewPrice = raw.fareSummary.reviewPrice as ReviewPrice;
    }
    if (raw.fareSummary.fareBreakup) {
      normalized.data.fareSummary.fareBreakup = raw.fareSummary.fareBreakup as FareBreakup;
    }
  }

  if (raw.reviewPrice || raw.fareBreakup) {
    if (raw.reviewPrice && !normalized.data.fareSummary.reviewPrice) {
      normalized.data.fareSummary.reviewPrice = raw.reviewPrice as ReviewPrice;
    }
    if (raw.fareBreakup) {
      normalized.data.fareSummary.fareBreakup = raw.fareBreakup as FareBreakup;
    }
  }

  if (raw.data?.reviewPrice || raw.data?.fareBreakup) {
    if (raw.data.reviewPrice && !normalized.data.fareSummary.reviewPrice) {
      normalized.data.fareSummary.reviewPrice = raw.data.reviewPrice as ReviewPrice;
    }
    if (raw.data.fareBreakup && !normalized.data.fareSummary.fareBreakup) {
      normalized.data.fareSummary.fareBreakup = raw.data.fareBreakup as FareBreakup;
    }
  }

  // Only include fareSummary in response if it has at least one field
  if (!normalized.data.fareSummary.reviewPrice && !normalized.data.fareSummary.fareBreakup) {
    delete normalized.data.fareSummary;
  }

  // Include coupon data if present (check both couponData and appliedCoupon field names)
  if (raw.couponData !== undefined) {
    normalized.data.couponData = raw.couponData as NonNullable<
      AddonSelectionResponse['data']['couponData']
    >;
  } else if (raw.appliedCoupon !== undefined) {
    // API returns appliedCoupon instead of couponData
    normalized.data.couponData = raw.appliedCoupon as NonNullable<
      AddonSelectionResponse['data']['couponData']
    >;
  }

  // Include seats data if present (check root level first, then nested in data)
  if (raw.seats !== undefined) {
    normalized.data.seats = raw.seats as { data: SeatsData };
  } else if (raw.data?.seats !== undefined) {
    normalized.data.seats = raw.data.seats as { data: SeatsData };
  }

  return normalized;
}

/**
 * Select addon (meals, baggage, coupons, etc.)
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function selectAddon(
  params: AddonSelectionRequestParams,
  body: AddonSelectionRequestBody,
): Promise<AddonSelectionResponse> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });

  if (params.contextHashKey !== undefined) {
    queryParams['contextHashKey'] = params.contextHashKey;
  }

  const url = buildUrl(FLIGHTS_ENDPOINTS.ADDON_SELECTION, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    // Always try to parse response body to get error message
    const rawData = (await response.json()) as RawAddonSelectionResponse & ApiErrorResponse;

    // Check if API returned an error in the response body (supports both formats)
    const errorMessage = extractErrorMessage(rawData);
    if (errorMessage) {
      throw new AddonSelectionError(errorMessage, response.status);
    }

    if (!response.ok) {
      throw new AddonSelectionError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }
    const normalizedResponse = normalizeAddonSelectionResponse(rawData);

    // Include additional response fields if present
    if (rawData.ctaList !== undefined) {
      (normalizedResponse as AddonSelectionResponse & { ctaList?: unknown }).ctaList =
        rawData.ctaList;
    }
    if (rawData.baggage !== undefined) {
      (normalizedResponse as AddonSelectionResponse & { baggage?: unknown }).baggage =
        rawData.baggage;
    }
    if (rawData.addons !== undefined) {
      (normalizedResponse as AddonSelectionResponse & { addons?: unknown }).addons = rawData.addons;
    }
    if (rawData.coupons !== undefined) {
      (normalizedResponse as AddonSelectionResponse & { coupons?: unknown }).coupons =
        rawData.coupons;
    }
    if (rawData.meals !== undefined) {
      (normalizedResponse as AddonSelectionResponse & { meals?: unknown }).meals = rawData.meals;
    }

    return normalizedResponse;
  } catch (error) {
    if (error instanceof AddonSelectionError) {
      throw error;
    }

    logger.error('[AddonSelectionClient] Request failed:', error);
    throw new AddonSelectionError('Failed to select addon', undefined, error);
  }
}

/**
 * Fetch meals
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchMeals(
  params: FetchMealsRequestParams,
): Promise<FlightsAddonMealsComponentData> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
    cc: params.cabinClass ?? 'E',
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.MEALS, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    // Always try to parse response body to get error message
    const data = (await response.json()) as FlightsAddonMealsComponentData & ApiErrorResponse;

    // Check if API returned an error in the response body (supports both formats)
    const errorMessage = extractErrorMessage(data);
    if (errorMessage) {
      throw new FetchMealsError(errorMessage, response.status);
    }

    if (!response.ok) {
      throw new FetchMealsError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof FetchMealsError) {
      throw error;
    }

    throw new FetchMealsError('Failed to fetch meals', undefined, error);
  }
}

/**
 * Fetch baggage
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchBaggage(
  params: FetchBaggageRequestParams,
): Promise<FlightsAddonBaggageComponentData> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
    cc: params.cabinClass ?? 'E',
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.BAGGAGE, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    // Always try to parse response body to get error message
    const data = (await response.json()) as FlightsAddonBaggageComponentData & ApiErrorResponse;

    // Check if API returned an error in the response body (supports both formats)
    const errorMessage = extractErrorMessage(data);
    if (errorMessage) {
      throw new FetchBaggageError(errorMessage, response.status);
    }

    if (!response.ok) {
      throw new FetchBaggageError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof FetchBaggageError) {
      throw error;
    }

    throw new FetchBaggageError('Failed to fetch baggage', undefined, error);
  }
}

/**
 * Fetch coupons
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchCoupons(
  params: FetchCouponsRequestParams,
): Promise<FlightsCouponsComponentData> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
    cc: params.cabinClass ?? 'E',
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.COUPONS, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    // Always try to parse response body to get error message
    const data = (await response.json()) as FlightsCouponsComponentData & ApiErrorResponse;

    // Check if API returned an error in the response body (supports both formats)
    const errorMessage = extractErrorMessage(data);
    if (errorMessage) {
      throw new FetchCouponsError(errorMessage, response.status);
    }

    if (!response.ok) {
      throw new FetchCouponsError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof FetchCouponsError) {
      throw error;
    }

    throw new FetchCouponsError('Failed to fetch coupons', undefined, error);
  }
}

/**
 * Fetch seats
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchSeats(
  params: FetchSeatsRequestParams,
): Promise<FlightsSeatsComponentData> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.SEATS, queryParams);
  const headers = buildHeaders();

  logger.warn('[FetchSeatsClient] Fetching seats:', {
    crId: params.crId,
    itineraryId: params.itineraryId,
    url,
  });

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    // Always try to parse response body to get error message
    const data = (await response.json()) as FlightsSeatsComponentData & ApiErrorResponse;

    // Check if API returned an error in the response body (supports both formats)
    const errorMessage = extractErrorMessage(data);
    if (errorMessage) {
      throw new FetchSeatsError(errorMessage, response.status);
    }

    if (!response.ok) {
      throw new FetchSeatsError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return data;
  } catch (error) {
    if (error instanceof FetchSeatsError) {
      throw error;
    }

    logger.error('[FetchSeatsClient] Request failed:', error);
    throw new FetchSeatsError('Failed to fetch seats', undefined, error);
  }
}
